import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Percent, Calculator, Copy, Trash2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const PercentageCalculator: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'basic' | 'increase' | 'change'>('basic');
  
  // Basic percentage calculation
  const [basicValue, setBasicValue] = useState('');
  const [basicPercent, setBasicPercent] = useState('');
  const [basicResult, setBasicResult] = useState('');

  // Percentage increase/decrease
  const [originalValue, setOriginalValue] = useState('');
  const [newValue, setNewValue] = useState('');
  const [changeResult, setChangeResult] = useState('');

  // What percent of X is Y
  const [totalValue, setTotalValue] = useState('');
  const [partValue, setPartValue] = useState('');
  const [percentResult, setPercentResult] = useState('');

  useSEO({
    title: 'Percentage Calculator - Calculate Percentages, Increases & Changes',
    description: 'Free online percentage calculator. Calculate percentages, percentage increases, decreases, and percentage changes with instant results.',
    keywords: 'percentage calculator, percent calculator, percentage increase, percentage decrease, percentage change',
    canonical: 'https://snap-tools.online/calc/percentage-calculator'
  });

  const calculateBasicPercentage = () => {
    const value = parseFloat(basicValue);
    const percent = parseFloat(basicPercent);
    
    if (!isNaN(value) && !isNaN(percent)) {
      const result = (value * percent) / 100;
      setBasicResult(result.toString());
    } else {
      setBasicResult('');
    }
  };

  const calculatePercentChange = () => {
    const original = parseFloat(originalValue);
    const newVal = parseFloat(newValue);
    
    if (!isNaN(original) && !isNaN(newVal) && original !== 0) {
      const change = ((newVal - original) / original) * 100;
      setChangeResult(change.toString());
    } else {
      setChangeResult('');
    }
  };

  const calculateWhatPercent = () => {
    const total = parseFloat(totalValue);
    const part = parseFloat(partValue);
    
    if (!isNaN(total) && !isNaN(part) && total !== 0) {
      const percent = (part / total) * 100;
      setPercentResult(percent.toString());
    } else {
      setPercentResult('');
    }
  };

  const copyResult = (result: string) => {
    if (result) {
      navigator.clipboard.writeText(result);
      toast.success('Result copied to clipboard');
    }
  };

  const clearAll = () => {
    setBasicValue('');
    setBasicPercent('');
    setBasicResult('');
    setOriginalValue('');
    setNewValue('');
    setChangeResult('');
    setTotalValue('');
    setPartValue('');
    setPercentResult('');
    toast.success('All fields cleared');
  };

  React.useEffect(() => {
    calculateBasicPercentage();
  }, [basicValue, basicPercent]);

  React.useEffect(() => {
    calculatePercentChange();
  }, [originalValue, newValue]);

  React.useEffect(() => {
    calculateWhatPercent();
  }, [totalValue, partValue]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <Percent className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            Percentage Calculator
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Calculate percentages, percentage increases, decreases, and changes with instant results 
            for all your mathematical needs.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-6 sm:mb-8"
        >
          <div className="bg-white rounded-xl p-1.5 shadow-lg border border-gray-100">
            <button
              onClick={() => setActiveTab('basic')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                activeTab === 'basic'
                  ? 'bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#8438ff] hover:bg-gray-50'
              }`}
            >
              Basic %
            </button>
            <button
              onClick={() => setActiveTab('increase')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                activeTab === 'increase'
                  ? 'bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#8438ff] hover:bg-gray-50'
              }`}
            >
              % Change
            </button>
            <button
              onClick={() => setActiveTab('change')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all duration-200 text-sm ${
                activeTab === 'change'
                  ? 'bg-gradient-to-r from-[#8438ff] to-[#7228ee] text-white shadow-md'
                  : 'text-gray-600 hover:text-[#8438ff] hover:bg-gray-50'
              }`}
            >
              What %
            </button>
          </div>
        </motion.div>

        {/* Calculator Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-5 sm:p-6 lg:p-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
              <Calculator className="h-5 w-5 mr-2 text-[#8438ff]" />
              {activeTab === 'basic' && 'Basic Percentage'}
              {activeTab === 'increase' && 'Percentage Change'}
              {activeTab === 'change' && 'What Percent'}
            </h3>
            <button
              onClick={clearAll}
              className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
              title="Clear all"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          {/* Basic Percentage Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <p className="text-gray-600 text-center">What is X% of Y?</p>
              <div className="grid md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Percentage (%)</label>
                  <input
                    type="number"
                    value={basicPercent}
                    onChange={(e) => setBasicPercent(e.target.value)}
                    placeholder="Enter percentage"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Of Value</label>
                  <input
                    type="number"
                    value={basicValue}
                    onChange={(e) => setBasicValue(e.target.value)}
                    placeholder="Enter value"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Result</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={basicResult}
                      readOnly
                      placeholder="Result"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg bg-gray-50"
                    />
                    {basicResult && (
                      <button
                        onClick={() => copyResult(basicResult)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-gray-500 hover:text-[#8438ff] transition-colors"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Percentage Change Tab */}
          {activeTab === 'increase' && (
            <div className="space-y-6">
              <p className="text-gray-600 text-center">Percentage change from A to B</p>
              <div className="grid md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Original Value</label>
                  <input
                    type="number"
                    value={originalValue}
                    onChange={(e) => setOriginalValue(e.target.value)}
                    placeholder="Enter original value"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">New Value</label>
                  <input
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Enter new value"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Change (%)</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={changeResult ? `${parseFloat(changeResult).toFixed(2)}%` : ''}
                      readOnly
                      placeholder="Result"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg bg-gray-50"
                    />
                    {changeResult && (
                      <button
                        onClick={() => copyResult(changeResult)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-gray-500 hover:text-[#8438ff] transition-colors"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {changeResult && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    {parseFloat(changeResult) > 0 ? 'Increase' : 'Decrease'}: {' '}
                    <span className={`font-semibold ${parseFloat(changeResult) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Math.abs(parseFloat(changeResult)).toFixed(2)}%
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* What Percent Tab */}
          {activeTab === 'change' && (
            <div className="space-y-6">
              <p className="text-gray-600 text-center">What percent of X is Y?</p>
              <div className="grid md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Total Value (X)</label>
                  <input
                    type="number"
                    value={totalValue}
                    onChange={(e) => setTotalValue(e.target.value)}
                    placeholder="Enter total value"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Part Value (Y)</label>
                  <input
                    type="number"
                    value={partValue}
                    onChange={(e) => setPartValue(e.target.value)}
                    placeholder="Enter part value"
                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Percentage</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={percentResult ? `${parseFloat(percentResult).toFixed(2)}%` : ''}
                      readOnly
                      placeholder="Result"
                      className="w-full p-3 border-2 border-gray-200 rounded-lg bg-gray-50"
                    />
                    {percentResult && (
                      <button
                        onClick={() => copyResult(percentResult)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-gray-500 hover:text-[#8438ff] transition-colors"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8 sm:mt-12"
        />
      </div>
    </div>
  );
};

export default PercentageCalculator;
